/**
 * The command bar: who you are talking to, and what you want.
 *
 * His words were "The Chat Panel down is so bad", and he is right for a reason worse than looks.
 * The bar was a wide field labelled "Ask MO OS anything..." whose Enter key ran a VAULT SEARCH and
 * put results in a sidebar. The only things that opened a conversation were the four chips beside
 * it. So the biggest, most inviting control on the board did not do the thing it said, and the
 * feature it advertised was reachable only by not using it.
 *
 * TWO CHATS, BOTH NAMED. He also said: add a droid to chat with, along with Zou, "you have 2 chats
 * anyway". That is exactly right and it was the other half of the problem. The overlay has always
 * had two lanes, `brain` and `zou`, and the bar exposed neither: it sent everything to the brain
 * and left Zou reachable only from a Chase row. Two lanes with two different jobs, presented as one
 * anonymous box, is a UI hiding half of itself.
 *
 * So: pick who, type, send. The persona decides the placeholder, the suggestions and the lane, and
 * search keeps its own button rather than stealing the Enter key.
 *
 * ON THE FACES. A droid for the brain and Zou's own plate for Zou. This is not decoration either:
 * two lanes that answer differently need to be told apart at a glance, and a name plus a face does
 * that faster than a label. The droid is the one that fetches and reports; Zou is the one that
 * writes. That division is real, so the pictures can carry it.
 */

import { useState, type FormEvent } from 'react';
import { Icon } from './Icon.js';
import { openAsk } from './ChatOverlay.js';
import type { MaterialKind } from './api.js';
import { usePaneState } from '../views/paneState.js';
import { useApp } from '../store.js';
import './commandDeck.css';

interface Suggestion {
  label: string;
  about: string;
  kind?: MaterialKind;
}

interface Persona {
  lane: 'brain' | 'zou';
  name: string;
  role: string;
  plate: string;
  placeholder: string;
  /** The verb on the send button. "Ask" and "Brief" are different acts and should read that way. */
  verb: string;
  suggestions: Suggestion[];
}

const PERSONAS: readonly Persona[] = [
  {
    lane: 'brain',
    name: 'MO',
    role: 'Knows the vault',
    plate: '/char-droid.webp',
    placeholder: 'Ask about anyone, any account, anything you owe...',
    verb: 'Ask',
    suggestions: [
      { label: 'What needs me today?', about: 'what needs me today' },
      { label: 'Prepare me for my next meeting', about: 'preparing me for my next meeting' },
      { label: 'Who have I neglected?', about: 'who I have neglected' },
      { label: 'What changed since yesterday?', about: 'what changed since yesterday' },
    ],
  },
  {
    lane: 'zou',
    name: 'Zou',
    role: 'Writes and composes',
    plate: '/crew-zou.webp',
    placeholder: 'Name the account or meeting, and say what you need written...',
    verb: 'Brief',
    /*
     * Each of these carries a `kind`, because Zou's kinds are a closed list on the server and
     * "write me whatever" produces the generic consulting mush that would make him stop using it.
     * The suggestion is therefore a real brief, not a prompt starter.
     */
    suggestions: [
      { label: 'Draft a follow-up email', about: '', kind: 'follow-up-email' },
      { label: 'Brief me before a meeting', about: '', kind: 'pre-meeting-briefing' },
      { label: 'Talking points', about: '', kind: 'talking-points' },
      { label: 'Proposal outline', about: '', kind: 'proposal-outline' },
    ],
  },
];

export function ChatBar(): JSX.Element {
  const [lane, setLane] = useState<'brain' | 'zou'>('brain');
  const [q, setQ] = useState('');
  const setSearchQuery = usePaneState((s) => s.setSearchQuery);
  const setSidebarTab = useApp((s) => s.setSidebarTab);

  const who = PERSONAS.find((p) => p.lane === lane) ?? PERSONAS[0]!;

  const send = (e: FormEvent): void => {
    e.preventDefault();
    const text = q.trim();
    if (text === '') return;
    openAsk({ lane: who.lane, about: text });
    setQ('');
  };

  /*
   * Search keeps a button and loses the Enter key. It is genuinely useful and it is NOT the same
   * act as asking: one finds a note by its words, the other spends about 25 seconds and a model
   * call to answer a question. Conflating them is what made the bar lie.
   */
  const find = (): void => {
    const text = q.trim();
    if (text === '') return;
    setSearchQuery(text);
    setSidebarTab('search');
  };

  const suggest = (s: Suggestion): void => {
    // An empty `about` means the suggestion needs a subject, so it lands in the box for him to
    // finish rather than sending a brief with no account in it.
    if (s.about === '') {
      openAsk({ lane: who.lane, about: q.trim(), kind: s.kind, hold: true });
      return;
    }
    openAsk({ lane: who.lane, about: s.about, kind: s.kind });
  };

  return (
    <div className="cbar">
      <div className="cbar-who" role="tablist" aria-label="Who to talk to">
        {PERSONAS.map((p) => (
          <button
            key={p.lane}
            type="button"
            role="tab"
            aria-selected={p.lane === lane}
            className="cbar-persona"
            data-on={p.lane === lane}
            onClick={() => setLane(p.lane)}
          >
            <span className="cbar-face">
              <img src={p.plate} alt="" width={216} height={162} loading="lazy" />
            </span>
            <span className="cbar-id">
              <span className="cbar-name">{p.name}</span>
              <span className="cbar-role">{p.role}</span>
            </span>
          </button>
        ))}
      </div>

      <form className="cbar-main" onSubmit={send}>
        <label className="cbar-label" htmlFor="cbar-input">
          Talking to {who.name}
        </label>
        <div className="cbar-row">
          <input
            id="cbar-input"
            className="cbar-input"
            type="text"
            value={q}
            placeholder={who.placeholder}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" className="cbar-send" disabled={q.trim() === ''}>
            <Icon name="spark" size={14} />
            {who.verb} {who.name}
          </button>
          <button type="button" className="cbar-find" onClick={find} disabled={q.trim() === ''}>
            <Icon name="search" size={14} />
            Find
          </button>
        </div>
        <div className="cbar-chips">
          {who.suggestions.map((s) => (
            <button key={s.label} type="button" className="cbar-chip" onClick={() => suggest(s)}>
              {s.label}
            </button>
          ))}
        </div>
      </form>
    </div>
  );
}
